import ChalkySticks from '@chalkysticks/sdk';
import Store from '@/Store';
import { ActionContext } from 'vuex';
import { IStoreState } from './State';

/**
 * @interface IPushTokenPayload
 */
interface IPushTokenPayload {
	appVersion: string;
	deviceId?: string;
	platform: string;
	token: string;
}

/**
 * @type ActionContext<S, R>
 */
type Context = ActionContext<IStoreState, IStoreState>;

/**
 * Actions are generally meant to be the entrypoint for modifying state.
 *
 * They are asynchronous calls that can perform other tasks and then
 * trigger a mutation (synchronous).
 *
 * @see https://vuex.vuejs.org/guide/actions.html
 *
 * @class Actions
 * @package Store/Module/Settings
 * @project ChalkySticks App
 */
const actions = {
	/**
	 * Add a new feed item to the feed structure
	 *
	 * @param Context context
	 * @param IFeedStructure payload
	 * @return void
	 */
	addFeedItem(context: Context, payload: IFeedStructure): void {
		context.commit('addFeedItem', payload);
	},

	/**
	 * @param Context context
	 * @param any payload
	 * @return void
	 */
	completeOnboarding(context: Context): void {
		context.commit('setOnboardingComplete', true);
	},

	/**
	 * @param Context context
	 * @param any payload
	 * @return void
	 */
	disableLocation(context: Context): void {
		context.commit('setUseLocation', false);
	},

	/**
	 * @param Context context
	 * @param any payload
	 * @return void
	 */
	enableLocation(context: Context, shouldEnable: boolean = true): void {
		context.commit('setUseLocation', shouldEnable);
	},

	/**
	 * Register push notification token with the backend
	 *
	 * @param Context context
	 * @param IPushTokenPayload payload
	 * @return Promise<void>
	 */
	async registerPushToken(context: Context, payload: IPushTokenPayload): Promise<void> {
		try {
			const generalModel = new ChalkySticks.Core.Model.Base();
			generalModel.setEndpoint('/push-notification');
			generalModel.set({
				app_version: payload.appVersion,
				device_id: payload.deviceId,
				platform: payload.platform,
				token: payload.token,
			});

			// Log our auth token
			ChalkySticks.Utility.Log.debug('push-notification', 'Auth token:', Store.getters['authentication/token']);

			// Send to API
			await generalModel.auth().save();

			// Store in local state
			context.commit('setPushToken', payload.token);

			if (payload.deviceId) {
				context.commit('setDeviceId', payload.deviceId);
			}

			ChalkySticks.Utility.Log.debug('push-notification', 'Successfully registered push token', payload);
		} catch (error) {
			ChalkySticks.Utility.Log.error('push-notification', 'Failed to register push token:', error);
		}
	},

	/**
	 * Remove a feed item by component name
	 *
	 * @param Context context
	 * @param string componentName
	 * @return void
	 */
	removeFeedItem(context: Context, componentName: string): void {
		context.commit('removeFeedItem', componentName);
	},

	/**
	 * Set the feed structure configuration
	 *
	 * @param Context context
	 * @param IFeedStructure[] payload
	 * @return void
	 */
	setFeedStructure(context: Context, payload: IFeedStructure[]): void {
		context.commit('setFeedStructure', payload);
	},

	/**
	 * Set the PWA install status (accepted/dismissed)
	 *
	 * @param Context context
	 * @param 'accepted' | 'dismissed' | null value
	 * @return void
	 */
	setPwaInstallStatus(context: Context, value: 'accepted' | 'dismissed' | null): void {
		context.commit('setPwaInstallStatus', value);
	},

	/**
	 * Update app configuration from API response
	 *
	 * @param Context context
	 * @param IAppConfig payload
	 * @return void
	 */
	updateConfig(context: Context, payload: IAppConfig): void {
		// Save the feed structure
		if (payload.feed_structure) {
			context.commit('setFeedStructure', payload.feed_structure);
		}

		// Save the features
		if (payload.features) {
			context.commit('setFeatures', payload.features);
		}

		// Save the app settings
		if (payload.settings) {
			context.commit('setAppSettings', payload.settings);
		}

		// Save the API version
		if (payload.version?.api) {
			context.commit('setApiVersion', payload.version.api);
		}
	},

	/**
	 * Enable or disable a feed item by component name
	 *
	 * @param Context context
	 * @param Object payload
	 * @return void
	 */
	updateFeedItemStatus(context: Context, payload: { component: string; enabled: boolean }): void {
		context.commit('updateFeedItemStatus', payload);
	},
};

export default actions;
